import { redirect } from '@tanstack/react-router';
import { authBaseURL } from '@/lib/auth-client';

export function getSafeAuthRedirectPath(redirectTo?: string | null) {
  if (!redirectTo || !redirectTo.startsWith('/') || redirectTo.startsWith('//')) {
    return '/';
  }

  return redirectTo;
}

/**
 * Returns an absolute URL on the current (admin) origin for use as a
 * better-auth `callbackURL` / `redirectTo`. Required for any flow where the
 * API performs a server-side redirect (OAuth callbacks, magic-link verify,
 * password-reset verify, email-verification verify). Without an absolute URL,
 * better-auth resolves the relative path against its `baseURL` (the API
 * origin) and the user lands on the API instead of the admin app.
 */
export function getSafeAuthRedirectURL(redirectTo?: string | null) {
  return `${window.location.origin}${getSafeAuthRedirectPath(redirectTo)}`;
}

// Params the server adds when it signs an OAuth request. The signature expires
// after 10 minutes, too short for email verification plus onboarding, so a
// resume link drops them and lets the authorize endpoint sign a fresh one.
const OAUTH_SIGNATURE_PARAMS = ['sig', 'exp', 'ba_iat', 'ba_pl', 'ba_param'];

const oauthAuthorizeURL = () => `${authBaseURL}/oauth2/authorize`;

/**
 * The authorize URL that resumes an MCP/OAuth sign-in, built from a page query
 * that carries the request (the login and consent pages); undefined otherwise.
 * Visiting it with a session continues to consent.
 */
export function getOAuthResumeURL(search: string): string | undefined {
  const params = new URLSearchParams(search);
  if (!params.has('client_id') || !params.has('response_type'))
    return undefined;
  for (const key of [...OAUTH_SIGNATURE_PARAMS, 'redirect']) params.delete(key);
  return `${oauthAuthorizeURL()}?${params}`;
}

export function isOAuthResumeURL(target?: string | null): target is string {
  return !!target && target.startsWith(`${oauthAuthorizeURL()}?`);
}

/** A better-auth `callbackURL` for a return target: the resume URL as-is, else a same-origin path. */
export function getAuthCallbackURL(redirectTo?: string | null, fallback = '/') {
  return isOAuthResumeURL(redirectTo)
    ? redirectTo
    : getSafeAuthRedirectURL(redirectTo ?? fallback);
}

/** Throw from `beforeLoad` to send an already-signed-in user on to their return target. */
export function redirectToAuthTarget(redirectTo?: string | null): never {
  if (isOAuthResumeURL(redirectTo)) {
    throw redirect({ href: redirectTo, reloadDocument: true });
  }
  throw redirect({ to: getSafeAuthRedirectPath(redirectTo) });
}
